/**
 * Pure projection from a scanRun + elapsed-time into a banner UI state.
 * Extracted from the component so the state-machine can be unit-tested
 * without React.
 */

export type ScanBannerKind =
  | 'hidden'
  | 'queued'
  | 'running'
  | 'deferred'
  | 'failed'
  | 'recent-success';

export interface ScanBannerState {
  kind: ScanBannerKind;
  title: string;
  detail?: string;
  evidenceDetail?: string;
  progressPercent?: number;
  retryAtMs?: number;
  errorMessage?: string;
  errorCode?: string;
  /**
   * True when the user can recover this state by re-running the scan. The
   * banner renders a "Try again" action when a retry handler is supplied.
   */
  retryable?: boolean;
  /**
   * Whether downstream "scan is happening right now" affordances (stat
   * pulses, skeletons) should be active. True for queued+running+deferred.
   */
  isLive: boolean;
}

interface ScanRunProjection {
  status: 'queued' | 'running' | 'deferred' | 'succeeded' | 'failed';
  profile: 'full' | 'onboarding';
  triggeredBy?:
    | 'user'
    | 'onboarding'
    | 'onboarding-followup'
    | 'manual-rescan'
    | 'webhook';
  phase?: string;
  filesProcessed?: number;
  totalFiles?: number;
  hardcodedFound?: number;
  tokensFound?: number;
  retryAfter?: number;
  errorMessage?: string;
  errorCode?: string;
  finishedAt?: number;
  scannerFactSummary?: ScannerFactSummary;
}

interface ScannerFactSummary {
  available: boolean;
  componentFacts: number;
  tokenFacts: number;
  diagnosticFacts: number;
  componentAdoptionFacts?: number;
  tokenAdoptionFacts?: number;
  warningDiagnostics: number;
  errorDiagnostics: number;
}

const RECENT_SUCCESS_MS = 60_000;
const numberFormatter = new Intl.NumberFormat('en-US');

function progressPercent(run: ScanRunProjection): number | undefined {
  if (!run.totalFiles || run.totalFiles <= 0) return undefined;
  const processed = Math.min(run.filesProcessed ?? 0, run.totalFiles);
  return Math.round((processed / run.totalFiles) * 100);
}

function runningDetail(run: ScanRunProjection): string {
  const hardcoded = run.hardcodedFound ?? 0;
  if (hardcoded > 0) {
    const noun = hardcoded === 1 ? 'issue' : 'issues';
    return `${numberFormatter.format(hardcoded)} ${noun} found so far`;
  }
  if (run.phase === 'tokens') return 'Reading design tokens';
  if (run.phase === 'extracting') return 'Extracting components';
  if (run.phase === 'hardcoded') return 'Scanning style files for token drift';
  return 'Inspecting repository';
}

function evidenceDetail(
  summary: ScannerFactSummary | null | undefined,
): string | undefined {
  if (!summary?.available) return undefined;

  const evidenceParts: string[] = [];
  if (summary.componentFacts > 0) {
    evidenceParts.push(
      `${numberFormatter.format(summary.componentFacts)} component ${summary.componentFacts === 1 ? 'fact' : 'facts'}`,
    );
  }
  if (summary.tokenFacts > 0) {
    evidenceParts.push(
      `${numberFormatter.format(summary.tokenFacts)} token ${summary.tokenFacts === 1 ? 'fact' : 'facts'}`,
    );
  }
  const adoptionFacts =
    (summary.componentAdoptionFacts ?? 0) + (summary.tokenAdoptionFacts ?? 0);
  if (adoptionFacts > 0) {
    evidenceParts.push(
      `${numberFormatter.format(adoptionFacts)} adoption ${adoptionFacts === 1 ? 'fact' : 'facts'}`,
    );
  }

  const evidenceText =
    evidenceParts.length > 0
      ? `Evidence indexed: ${evidenceParts.join(', ')}.`
      : 'Evidence indexed.';

  if (summary.diagnosticFacts <= 0) return evidenceText;

  const diagnostics = `${numberFormatter.format(summary.diagnosticFacts)} scanner ${summary.diagnosticFacts === 1 ? 'diagnostic' : 'diagnostics'}`;
  const severeDiagnostics =
    summary.errorDiagnostics > 0
      ? `${numberFormatter.format(summary.errorDiagnostics)} error${summary.errorDiagnostics === 1 ? '' : 's'}`
      : summary.warningDiagnostics > 0
        ? `${numberFormatter.format(summary.warningDiagnostics)} warning${summary.warningDiagnostics === 1 ? '' : 's'}`
        : undefined;

  return severeDiagnostics
    ? `${evidenceText} ${diagnostics}, including ${severeDiagnostics}.`
    : `${evidenceText} ${diagnostics}.`;
}

export function deriveScanBannerState({
  run,
  now,
}: {
  run: ScanRunProjection | null | undefined;
  now: number;
}): ScanBannerState {
  if (!run) return { kind: 'hidden', title: '', isLive: false };

  // Historical onboarding follow-up runs were background enrichment after the
  // wizard had already produced usable data. Do not let those runs make a
  // completed onboarding look incomplete or broken on the dashboard.
  if (run.triggeredBy === 'onboarding-followup') {
    return { kind: 'hidden', title: '', isLive: false };
  }

  // Onboarding scans own their own UX inside the wizard; do not show the
  // dashboard banner while one is in flight.
  if (
    run.profile === 'onboarding' &&
    (run.status === 'queued' || run.status === 'running')
  ) {
    return { kind: 'hidden', title: '', isLive: false };
  }

  if (run.status === 'queued') {
    return {
      kind: 'queued',
      title: 'Background scan starting...',
      detail: 'Looking for design system drift in your repository.',
      isLive: true,
    };
  }

  if (run.status === 'running') {
    return {
      kind: 'running',
      title: 'Scanning repository...',
      detail: runningDetail(run),
      evidenceDetail: evidenceDetail(run.scannerFactSummary),
      progressPercent: progressPercent(run),
      isLive: true,
    };
  }

  if (run.status === 'deferred') {
    return {
      kind: 'deferred',
      title: 'Paused by GitHub rate limit',
      detail: 'Fragments will retry automatically once the limit resets.',
      retryAtMs: run.retryAfter,
      isLive: true,
    };
  }

  if (run.status === 'failed') {
    const stalled = run.errorCode === 'SCAN_STALLED';
    return {
      kind: 'failed',
      title: "Scan didn't finish",
      detail: stalled
        ? 'The scan stopped before completing. Run it again to start fresh.'
        : 'Your last scan ran into an error before completing.',
      errorMessage: run.errorMessage,
      errorCode: run.errorCode,
      retryable: true,
      isLive: false,
    };
  }

  if (
    run.status === 'succeeded' &&
    run.finishedAt &&
    now - run.finishedAt <= RECENT_SUCCESS_MS
  ) {
    return {
      kind: 'recent-success',
      title: 'Scan complete',
      detail:
        (run.hardcodedFound ?? 0) > 0
          ? `Found ${run.hardcodedFound} issue${run.hardcodedFound === 1 ? '' : 's'} on this pass.`
          : 'No new issues detected.',
      evidenceDetail: evidenceDetail(run.scannerFactSummary),
      isLive: false,
    };
  }

  return { kind: 'hidden', title: '', isLive: false };
}
